/**
 * InvoiceActivity — filterable log of every invoice file that has passed
 * through the system (downloaded via Gmail watcher, uploaded manually, or
 * placed in a drop-zone).
 *
 * Filters: carrier · date range · invoice type · import status
 * Table:   filename · carrier · type · status · source · rows · when
 */

import { useState, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Card } from '../components/Card'
import { Icon } from '../components/Icon'
import { relativeTime } from '../lib/money'
import { useAuth, authHeaders } from '../lib/auth'
import { useTranslation } from 'react-i18next'

// ── Types ─────────────────────────────────────────────────────────────────────

interface ActivityRow {
  id:              number
  carrier:         string
  filename:        string
  source:          string
  invoice_type:    string | null
  import_status:   string
  downloaded_at:   string
  imported_at:     string | null
  rows_read:       number | null
  rows_inserted:   number | null
  import_error:    string | null
  sender_email:    string | null
  email_subject:   string | null
  file_size_bytes: number | null
}

interface FilterOptions {
  carriers: string[]
  types:    { value: string; label: string }[]
  statuses: string[]
}

interface ActivityData {
  total:   number
  limit:   number
  offset:  number
  rows:    ActivityRow[]
  filters: FilterOptions
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function statusPill(s: string) {
  switch (s) {
    case 'IMPORTED': return <span className="pill pill-mint text-[10px]">IMPORTED</span>
    case 'FAILED':   return <span className="pill pill-flame text-[10px]">FAILED</span>
    case 'PENDING':  return <span className="pill pill-honey text-[10px]">PENDING</span>
    default:         return <span className="pill pill-ink text-[10px]">{s}</span>
  }
}

function sourcePill(s: string) {
  switch (s) {
    case 'gmail':  return <span className="pill pill-royal text-[10px]">EMAIL</span>
    case 'portal': return <span className="pill pill-sky2  text-[10px]">PORTAL</span>
    default:       return <span className="pill pill-ink   text-[10px]">MANUAL</span>
  }
}

function fmtBytes(b: number | null): string {
  if (!b) return '—'
  if (b < 1024)        return `${b} B`
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`
  return `${(b / 1024 / 1024).toFixed(1)} MB`
}

function fmtAge(iso: string | null): string {
  if (!iso) return '—'
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000)
  return relativeTime(mins)
}

const PAGE_SIZE = 50

// ── Main component ─────────────────────────────────────────────────────────────

export function InvoiceActivityTab() {
  const { t } = useTranslation()
  const { token } = useAuth()

  // Filter state
  const [carrier, setCarrier]   = useState('')
  const [invType, setInvType]   = useState('')
  const [status,  setStatus]    = useState('')
  const [from,    setFrom]      = useState('')
  const [to,      setTo]        = useState('')
  const [offset,  setOffset]    = useState(0)

  // Reset pagination when filters change
  const applyFilter = useCallback((setter: (v: string) => void) => (v: string) => {
    setter(v)
    setOffset(0)
  }, [])

  // Build query string
  const qs = new URLSearchParams()
  if (carrier) qs.set('carrier', carrier)
  if (invType) qs.set('type',    invType)
  if (status)  qs.set('status',  status)
  if (from)    qs.set('from',    from)
  if (to)      qs.set('to',      to)
  qs.set('limit',  String(PAGE_SIZE))
  qs.set('offset', String(offset))

  const { data, isLoading } = useQuery<ActivityData>({
    queryKey: ['invoice-activity', carrier, invType, status, from, to, offset],
    queryFn:  () => fetch(`/api/invoice-activity?${qs}`, { headers: authHeaders(token) })
                      .then(r => r.json()),
  })

  const filterOpts = data?.filters
  const rows       = data?.rows ?? []
  const total      = data?.total ?? 0
  const totalPages = Math.ceil(total / PAGE_SIZE)
  const page       = Math.floor(offset / PAGE_SIZE) + 1

  const hasFilters = !!(carrier || invType || status || from || to)

  function clearFilters() {
    setCarrier(''); setInvType(''); setStatus('')
    setFrom(''); setTo(''); setOffset(0)
  }

  return (
    <div className="space-y-5">

      {/* Filter bar */}
      <Card className="p-4">
        <div className="flex flex-wrap gap-3 items-end">

          {/* Carrier */}
          <div className="flex flex-col gap-1 min-w-[140px]">
            <label className="text-[11px] font-semibold text-ink-500 uppercase tracking-wider">Carrier</label>
            <select
              value={carrier}
              onChange={e => applyFilter(setCarrier)(e.target.value)}
              className="px-2.5 py-1.5 border border-ink-200 rounded-lg text-[13px] bg-white text-ink-700"
            >
<option value="">{t('invoice_activity.all_carriers')}</option>
              {filterOpts?.carriers.map(c => (
                <option key={c} value={c}>{c.toUpperCase()}</option>
              ))}
            </select>
          </div>

          {/* Type */}
          <div className="flex flex-col gap-1 min-w-[160px]">
            <label className="text-[11px] font-semibold text-ink-500 uppercase tracking-wider">Invoice type</label>
            <select
              value={invType}
              onChange={e => applyFilter(setInvType)(e.target.value)}
              className="px-2.5 py-1.5 border border-ink-200 rounded-lg text-[13px] bg-white text-ink-700"
            >
<option value="">{t('invoice_activity.all_types')}</option>
              {filterOpts?.types.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          {/* Status */}
          <div className="flex flex-col gap-1 min-w-[130px]">
            <label className="text-[11px] font-semibold text-ink-500 uppercase tracking-wider">Status</label>
            <select
              value={status}
              onChange={e => applyFilter(setStatus)(e.target.value)}
              className="px-2.5 py-1.5 border border-ink-200 rounded-lg text-[13px] bg-white text-ink-700"
            >
<option value="">{t('invoice_activity.all_statuses')}</option>
              {filterOpts?.statuses.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          {/* Date range */}
          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-semibold text-ink-500 uppercase tracking-wider">From</label>
            <input
              type="date"
              value={from}
              onChange={e => applyFilter(setFrom)(e.target.value)}
              className="px-2.5 py-1.5 border border-ink-200 rounded-lg text-[13px] bg-white text-ink-700"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-semibold text-ink-500 uppercase tracking-wider">To</label>
            <input
              type="date"
              value={to}
              onChange={e => applyFilter(setTo)(e.target.value)}
              className="px-2.5 py-1.5 border border-ink-200 rounded-lg text-[13px] bg-white text-ink-700"
            />
          </div>

          {/* Clear */}
          {hasFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-ink-200 text-[13px] text-ink-600 hover:bg-ink-50 mt-auto"
            >
              <Icon name="x" className="w-3.5 h-3.5" />
              Clear
            </button>
          )}

          {/* Result count */}
          <div className="ml-auto mt-auto text-[12px] text-ink-400">
            {isLoading ? 'Loading…' : `${total.toLocaleString()} file${total !== 1 ? 's' : ''}`}
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card className="overflow-hidden">
        <table className="w-full text-[13px]">
          <thead className="bg-ink-50 text-ink-500 text-[10px] uppercase tracking-wider">
            <tr>
              <th className="text-left font-semibold py-2.5 px-4">File</th>
              <th className="text-left font-semibold py-2.5">Carrier</th>
              <th className="text-left font-semibold py-2.5">Type</th>
              <th className="text-left font-semibold py-2.5">Status</th>
              <th className="text-left font-semibold py-2.5">Source</th>
              <th className="text-right font-semibold py-2.5">Rows</th>
              <th className="text-right font-semibold py-2.5">Size</th>
              <th className="text-left font-semibold py-2.5 pr-4">Downloaded</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={8} className="py-10 text-center text-ink-400">Loading…</td></tr>
            )}
            {!isLoading && rows.length === 0 && (
              <tr><td colSpan={8} className="py-10 text-center text-ink-400">No records match the current filters.</td></tr>
            )}
            {rows.map(r => (
              <tr key={r.id} className="border-t border-ink-100 hover:bg-ink-50/50">

                {/* Filename — truncated, hover shows full + subject */}
                <td className="py-2.5 px-4 max-w-[240px]">
                  <div className="mono text-[11.5px] truncate" title={r.filename}>{r.filename}</div>
                  {r.email_subject && (
                    <div className="text-[10.5px] text-ink-400 truncate mt-0.5" title={r.email_subject}>
                      {r.email_subject}
                    </div>
                  )}
                  {r.import_error && (
                    <div className="text-[10.5px] text-flame-600 truncate mt-0.5" title={r.import_error}>
                      ⚠ {r.import_error}
                    </div>
                  )}
                </td>

                {/* Carrier */}
                <td className="py-2.5">
                  <span className="pill pill-ink">{r.carrier.toUpperCase()}</span>
                </td>

                {/* Invoice type */}
                <td className="py-2.5 text-[12px] text-ink-600">
                  {r.invoice_type
                    ? <span className="inline-flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-royal-400 shrink-0" />
                        {r.invoice_type.replace('_', ' ')}
                      </span>
                    : <span className="text-ink-300">—</span>
                  }
                </td>

                {/* Status */}
                <td className="py-2.5">{statusPill(r.import_status)}</td>

                {/* Source */}
                <td className="py-2.5">{sourcePill(r.source)}</td>

                {/* Rows inserted / read */}
                <td className="py-2.5 text-right tabular-nums text-ink-600">
                  {r.rows_inserted !== null
                    ? <span title={`${r.rows_read ?? '?'} read`}>{r.rows_inserted.toLocaleString()}</span>
                    : <span className="text-ink-300">—</span>
                  }
                </td>

                {/* File size */}
                <td className="py-2.5 text-right tabular-nums text-ink-400 text-[11.5px]">
                  {fmtBytes(r.file_size_bytes)}
                </td>

                {/* Downloaded at */}
                <td className="py-2.5 pr-4 text-ink-400 text-[11.5px] whitespace-nowrap">
                  {fmtAge(r.downloaded_at)}
                  {r.sender_email && (
                    <div className="text-[10.5px] truncate max-w-[140px]" title={r.sender_email}>
                      {r.sender_email}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination */}
        {total > PAGE_SIZE && (
          <div className="px-4 py-3 border-t border-ink-100 flex items-center justify-between text-[12px] text-ink-500">
            <span>Page {page} of {totalPages} · {total.toLocaleString()} total</span>
            <div className="flex gap-2">
              <button
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                className="px-3 py-1 border border-ink-200 rounded-lg disabled:opacity-40 hover:bg-ink-50"
              >
                ← Prev
              </button>
              <button
                disabled={offset + PAGE_SIZE >= total}
                onClick={() => setOffset(offset + PAGE_SIZE)}
                className="px-3 py-1 border border-ink-200 rounded-lg disabled:opacity-40 hover:bg-ink-50"
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </Card>
    </div>
  )
}
